// Tiny fetch wrapper for the dashboard. Sends/receives JSON and folds the
// non-2xx case into a typed error rather than the caller having to remember
// to check `response.ok`.

import type {
  ApiStyleguide,
  ApiElementGuide,
  ApiElementMarking,
  ApiTokenSummary,
  CapturedNode,
} from "@clarity/shared";
export type { ApiStyleguide, ApiElementGuide } from "@clarity/shared";

export type ApiUser = { id: string; email: string; name: string };

export type ApiProject = {
  id: string;
  name: string;
  targetUrl: string;
  shareToken: string;
  archivedAt: number | null;
  createdAt: number;
  // Attribution — every Avista owner sees every project, so the creator
  // is surfaced for context. Null if the creator's row has been removed.
  createdByEmail: string | null;
  createdByName: string | null;
  // Comment activity counters for the list view.
  openCommentCount: number;
  resolvedCommentCount: number;
};

export type ApiShareLookup = {
  project: { id: string; name: string; targetUrl: string };
};

export type ApiCommentPosition = {
  viewportWidth: number;
  pageX: number;
  pageY: number;
  selector: string | null;
  offsetX: number;
  offsetY: number;
};

export type ApiComment = {
  id: string;
  parentId: string | null;
  pageUrl: string;
  position: ApiCommentPosition;
  authorName: string;
  authorEmail: string;
  body: string;
  status: "open" | "resolved";
  createdAt: number;
  resolvedAt: number | null;
};

export type CreateCommentInput = {
  pageUrl: string;
  parentId?: string | null;
  authorName: string;
  authorEmail: string;
  body: string;
  position: ApiCommentPosition;
};

export type ApiPage = {
  id: string;
  name: string;
  path: string;
  fullUrl: string;
  orderIndex: number;
  openCommentCount: number;
  totalCommentCount: number;
  hidden: boolean;
};

export type ApiInvitee = {
  id: string;
  name: string;
  email: string;
  status: "active" | "revoked";
  createdAt: number;
  lastSeenAt: number | null;
};

export type ApiClientMe = { email: string; name: string | null };

export type ApiClientProject = {
  id: string;
  name: string;          // invitee-specific display name
  projectName: string;
  targetUrl: string;
  shareToken: string;
  openCommentCount: number;
};

// Owner-only admin surface.
export type ApiAdminOwner = {
  id: string;
  email: string;
  name: string;
  createdAt: number;
  projectCount: number;
};

export type ApiAdminCustomer = {
  email: string;
  name: string;
  projectCount: number;
  activeCount: number;
  revokedCount: number;
  lastSeenAt: number | null;
};

export type ApiAdminOwnerProject = {
  id: string;
  name: string;
  targetUrl: string;
  archivedAt: number | null;
  createdAt: number;
};

// Owner's personal API token. The secret itself is only on the create
// response, never on a list.
export type ApiToken = ApiTokenSummary;

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown) {
    super(typeof body === "object" && body && "error" in body ? String((body as { error: unknown }).error) : `http_${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  const body: unknown = text ? safeJson(text) : null;
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export const api = {
  me: () => request<{ user: ApiUser | null }>("/api/me"),
  signup: (input: { email: string; password: string; name: string }) =>
    request<{ user: ApiUser }>("/api/auth/signup", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  login: (input: { email: string; password: string }) =>
    request<{ user: ApiUser }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  listProjects: (opts?: { includeArchived?: boolean }) =>
    request<{ projects: ApiProject[] }>(
      opts?.includeArchived ? "/api/projects?includeArchived=1" : "/api/projects",
    ),
  createProject: (input: { name: string; targetUrl: string; discover?: boolean }) =>
    request<{
      project: ApiProject;
      // Only present when discover=true was sent. "failed" means the crawl
      // didn't produce any extra pages (the project still has its Home page).
      discoveryStatus?: "ok" | "partial" | "failed";
    }>("/api/projects", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  getProject: (id: string) => request<{ project: ApiProject }>(`/api/projects/${id}`),
  archiveProject: (id: string) =>
    request<{ ok: true }>(`/api/projects/${id}/archive`, { method: "POST" }),
  unarchiveProject: (id: string) =>
    request<{ ok: true }>(`/api/projects/${id}/unarchive`, { method: "POST" }),
  rotateShareToken: (id: string) =>
    request<{ project: ApiProject }>(`/api/projects/${id}/share/rotate`, { method: "POST" }),

  shareLookup: (token: string) => request<ApiShareLookup>(`/api/share/${token}`),

  // Comments — both surfaces. Reads can come from either; writes go through
  // the share token (owners use their project's own share token to post).
  listCommentsByShare: (token: string, pageUrl?: string) =>
    request<{ comments: ApiComment[] }>(
      `/api/share/${token}/comments${pageUrl ? `?pageUrl=${encodeURIComponent(pageUrl)}` : ""}`,
    ),
  listCommentsByProject: (projectId: string, pageUrl?: string) =>
    request<{ comments: ApiComment[] }>(
      `/api/projects/${projectId}/comments${pageUrl ? `?pageUrl=${encodeURIComponent(pageUrl)}` : ""}`,
    ),
  createComment: (token: string, input: CreateCommentInput) =>
    request<{ comment: ApiComment }>(`/api/share/${token}/comments`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  resolveComment: (projectId: string, commentId: string) =>
    request<{ comment: ApiComment }>(
      `/api/projects/${projectId}/comments/${commentId}/resolve`,
      { method: "POST" },
    ),
  reopenComment: (projectId: string, commentId: string) =>
    request<{ comment: ApiComment }>(
      `/api/projects/${projectId}/comments/${commentId}/reopen`,
      { method: "POST" },
    ),
  deleteComment: (projectId: string, commentId: string) =>
    request<{ ok: true }>(`/api/projects/${projectId}/comments/${commentId}`, {
      method: "DELETE",
    }),
  editCommentByOwner: (projectId: string, commentId: string, body: string) =>
    request<{ comment: ApiComment }>(
      `/api/projects/${projectId}/comments/${commentId}`,
      { method: "PATCH", body: JSON.stringify({ body }) },
    ),
  updateCommentPosition: (
    projectId: string,
    commentId: string,
    position: ApiCommentPosition,
  ) =>
    request<{ comment: ApiComment }>(
      `/api/projects/${projectId}/comments/${commentId}/position`,
      { method: "PATCH", body: JSON.stringify({ position }) },
    ),
  editCommentByShare: (
    token: string,
    commentId: string,
    input: { body: string; authorEmail: string },
  ) =>
    request<{ comment: ApiComment }>(
      `/api/share/${token}/comments/${commentId}`,
      { method: "PATCH", body: JSON.stringify(input) },
    ),

  listPagesByProject: (projectId: string) =>
    request<{ pages: ApiPage[] }>(`/api/projects/${projectId}/pages`),
  listPagesByShare: (token: string) =>
    request<{ pages: ApiPage[] }>(`/api/share/${token}/pages`),
  createPage: (projectId: string, input: { name: string; path: string }) =>
    request<{ page: ApiPage }>(`/api/projects/${projectId}/pages`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  renamePage: (projectId: string, pageId: string, name: string) =>
    request<{ page: ApiPage }>(`/api/projects/${projectId}/pages/${pageId}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),
  setPageVisibility: (projectId: string, pageId: string, hidden: boolean) =>
    request<{ page: ApiPage }>(
      `/api/projects/${projectId}/pages/${pageId}/visibility`,
      { method: "POST", body: JSON.stringify({ hidden }) },
    ),
  deletePage: (projectId: string, pageId: string) =>
    request<{ ok: true }>(`/api/projects/${projectId}/pages/${pageId}`, {
      method: "DELETE",
    }),
  reorderPages: (projectId: string, pageIds: string[]) =>
    request<{ pages: ApiPage[] }>(`/api/projects/${projectId}/pages/reorder`, {
      method: "POST",
      body: JSON.stringify({ pageIds }),
    }),
  rediscoverPages: (projectId: string) =>
    request<{
      added: ApiPage[];
      skipped: number;
      status: "ok" | "partial" | "failed";
    }>(`/api/projects/${projectId}/pages/discover`, { method: "POST" }),

  // Owner: invitee management for a project.
  listInvitees: (projectId: string) =>
    request<{ invitees: ApiInvitee[] }>(`/api/projects/${projectId}/invitees`),
  addInvitee: (projectId: string, input: { name: string; email: string }) =>
    request<{ invitee: ApiInvitee }>(`/api/projects/${projectId}/invitees`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  resendInvitee: (projectId: string, inviteeId: string) =>
    request<{ ok: true }>(
      `/api/projects/${projectId}/invitees/${inviteeId}/resend`,
      { method: "POST" },
    ),
  revokeInvitee: (projectId: string, inviteeId: string) =>
    request<{ ok: true }>(
      `/api/projects/${projectId}/invitees/${inviteeId}/revoke`,
      { method: "POST" },
    ),

  // Client portal — public sign-in (magic link) + authed endpoints.
  // shareToken is optional; when present it lets a brand-new reviewer (no
  // prior invite or comment history) sign up directly from a share link.
  clientLogin: (email: string, shareToken?: string) =>
    request<{ ok: true }>("/api/c/login", {
      method: "POST",
      body: JSON.stringify(shareToken ? { email, shareToken } : { email }),
    }),
  clientConsumeToken: (token: string, shareToken?: string) =>
    request<{ me: ApiClientMe }>("/api/c/consume", {
      method: "POST",
      body: JSON.stringify(shareToken ? { token, shareToken } : { token }),
    }),
  clientMe: () => request<{ me: ApiClientMe | null }>("/api/c/me"),
  clientLogout: () => request<{ ok: true }>("/api/c/logout", { method: "POST" }),
  clientListProjects: () =>
    request<{ projects: ApiClientProject[] }>("/api/c/projects"),

  // Admin (owner-only). Both populations live under /api/admin.
  adminListOwners: () =>
    request<{ owners: ApiAdminOwner[] }>("/api/admin/owners"),
  adminUpdateOwner: (id: string, input: { name: string }) =>
    request<{ owner: ApiAdminOwner }>(`/api/admin/owners/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  adminDeleteOwner: (id: string) =>
    request<{ ok: true; deletedSelf: boolean }>(`/api/admin/owners/${id}`, {
      method: "DELETE",
    }),
  adminListCustomers: () =>
    request<{ customers: ApiAdminCustomer[] }>("/api/admin/customers"),
  adminRevokeCustomer: (email: string) =>
    request<{ ok: true; revokedCount: number }>(
      `/api/admin/customers/${encodeURIComponent(email)}/revoke`,
      { method: "POST" },
    ),
  adminListOwnerProjects: (ownerId: string) =>
    request<{ projects: ApiAdminOwnerProject[] }>(
      `/api/admin/owners/${ownerId}/projects`,
    ),
  adminDeleteProject: (id: string) =>
    request<{ ok: true }>(`/api/admin/projects/${id}`, { method: "DELETE" }),

  // Owner's personal API tokens.
  listApiTokens: () => request<{ tokens: ApiToken[] }>("/api/tokens"),
  createApiToken: (input: { name: string }) =>
    request<{ token: ApiToken; secret: string }>("/api/tokens", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  revokeApiToken: (id: string) =>
    request<{ ok: true }>(`/api/tokens/${id}/revoke`, { method: "POST" }),

  // Phase 10 — Styleguide + Element Guide. Both surfaces are on-demand
  // extractions cached one row per project.
  getStyleguide: (projectId: string) =>
    request<{ styleguide: ApiStyleguide }>(
      `/api/projects/${projectId}/styleguide`,
    ),
  generateStyleguide: (projectId: string) =>
    request<{ styleguide: ApiStyleguide }>(
      `/api/projects/${projectId}/styleguide/generate`,
      { method: "POST" },
    ),
  getElementGuide: (projectId: string) =>
    request<{ elementGuide: ApiElementGuide }>(
      `/api/projects/${projectId}/elements`,
    ),
  generateElementGuide: (projectId: string) =>
    request<{ elementGuide: ApiElementGuide }>(
      `/api/projects/${projectId}/elements/generate`,
      { method: "POST" },
    ),

  // Phase 15 — AI "Copy as Breakdance". Sends a Code Mode subtree
  // capture; returns a ready-to-paste Breakdance clipboard string.
  // pageUrl lets the worker apply owner element tags for the page.
  translateBreakdance: (projectId: string, tree: CapturedNode, pageUrl?: string) =>
    request<{ clipboard: string }>(
      `/api/projects/${projectId}/breakdance/translate`,
      { method: "POST", body: JSON.stringify({ tree, pageUrl }) },
    ),

  // Phase 18 — owner Breakdance element tags, keyed by stable selector.
  listElementMarkings: (projectId: string, pageUrl: string) =>
    request<{ markings: ApiElementMarking[] }>(
      `/api/projects/${projectId}/element-markings?pageUrl=${encodeURIComponent(pageUrl)}`,
    ),
  setElementMarking: (
    projectId: string,
    input: { pageUrl: string; selector: string; breakdanceType: string; label?: string },
  ) =>
    request<{ marking: ApiElementMarking }>(
      `/api/projects/${projectId}/element-markings`,
      { method: "PUT", body: JSON.stringify(input) },
    ),
  clearElementMarking: (
    projectId: string,
    input: { pageUrl: string; selector: string },
  ) =>
    request<{ ok: true }>(`/api/projects/${projectId}/element-markings`, {
      method: "DELETE",
      body: JSON.stringify(input),
    }),
};
